import { useState } from "react"

import { Edit } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material'

import { handleApiError, usersService, UserType, type UpdateUserRequest, type User, type UserSource } from "../../api"
import { ROLE_OPTIONS, type UserRole } from "../../constants/roles"
import { isAdminUser } from "../../lib/user-utils"

interface UpdateUserModalProps {
  user: User
  currentUser: User
  onUserUpdated?: () => void
}

const USER_SOURCE_OPTIONS: { value: UserSource; label: string }[] = [
  { value: 'direct', label: 'Direct' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'app', label: 'App' },
]

const USER_TYPE_OPTIONS: { value: UserType; label: string }[] = [
  { value: UserType.REGULAR, label: 'Regular' },
  { value: UserType.CONTENT_ONLY, label: 'Content Only' },
]

export function UpdateUserModal({ user, currentUser, onUserUpdated }: UpdateUserModalProps) {
  const [isUpdating, setIsUpdating] = useState(false)
  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [formData, setFormData] = useState<UpdateUserRequest>({})
  const [pendingUpdates, setPendingUpdates] = useState<UpdateUserRequest>({})
  const [error, setError] = useState<string | null>(null)

  const isAdmin = isAdminUser(currentUser)

  const handleOpen = () => {
    setFormData({
      email: user.email,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      isVerified: user.isVerified,
      userType: user.userType,
      userSource: user.userSource,
      externalProfileId: user.externalProfileId || '',
      countryCode: user.countryCode,
    })
    setError(null)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setError(null)
  }

  const prepareUpdate = () => {
    const updates: UpdateUserRequest = {}

    if (formData.email && formData.email !== user.email) {
      updates.email = formData.email
    }
    if (formData.firstName !== (user.firstName || '')) {
      updates.firstName = formData.firstName || undefined
    }
    if (formData.lastName !== (user.lastName || '')) {
      updates.lastName = formData.lastName || undefined
    }
    if (formData.role && formData.role !== user.role) {
      updates.role = formData.role
    }
    if (formData.isVerified !== undefined && formData.isVerified !== user.isVerified) {
      updates.isVerified = formData.isVerified
    }
    if (formData.userType !== undefined && formData.userType !== user.userType) {
      updates.userType = formData.userType
    }
    if (formData.userSource && formData.userSource !== user.userSource) {
      updates.userSource = formData.userSource
    }
    if (formData.externalProfileId !== (user.externalProfileId || '')) {
      updates.externalProfileId = formData.externalProfileId || undefined
    }
    if (formData.countryCode && formData.countryCode !== user.countryCode) {
      updates.countryCode = formData.countryCode
    }

    if (Object.keys(updates).length === 0) {
      setError('No changes to save')
      return
    }

    setPendingUpdates(updates)
    setConfirmOpen(true)
  }

  const handleConfirmUpdate = async () => {
    try {
      setIsUpdating(true)
      setError(null)

      await usersService.updateUser(user.id, pendingUpdates)

      setConfirmOpen(false)
      setOpen(false)
      if (onUserUpdated) {
        onUserUpdated()
      }
    } catch (err) {
      setError(handleApiError(err))
      setConfirmOpen(false)
    } finally {
      setIsUpdating(false)
    }
  }

  const formatFieldName = (field: string): string => {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'number') return String(value)
    return String(value)
  }

  return (
    <>
      <Button
        size="small"
        variant="contained"
        startIcon={<Edit />}
        onClick={handleOpen}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          minWidth: 'auto',
          px: 1.5,
          py: 0.5,
          height: 28,
          backgroundColor: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        }}
      >
        Update User
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit />
            Update User
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update user information. Only changed fields will be submitted.
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                fullWidth
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth disabled={!isAdmin}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role || user.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                  label="Role"
                >
                  {ROLE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!isAdmin && (
                <Typography variant="caption" color="text.secondary">
                  Only admins can update roles
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="First Name"
                value={formData.firstName || ''}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Last Name"
                value={formData.lastName || ''}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Country Code"
                value={formData.countryCode || ''}
                onChange={(e) => setFormData({ ...formData, countryCode: e.target.value.toUpperCase() })}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 2 } }}
                helperText="2-letter country code (e.g., US, GB)"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={formData.userType ?? user.userType}
                  onChange={(e) => setFormData({ ...formData, userType: Number(e.target.value) as UserType })}
                  label="User Type"
                >
                  {USER_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>User Source</InputLabel>
                <Select
                  value={formData.userSource || user.userSource}
                  onChange={(e) => setFormData({ ...formData, userSource: e.target.value as UserSource })}
                  label="User Source"
                >
                  {USER_SOURCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="External Profile ID"
                value={formData.externalProfileId || ''}
                onChange={(e) => setFormData({ ...formData, externalProfileId: e.target.value })}
                fullWidth
                helperText="External profile ID from marketing platform"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isVerified ?? user.isVerified}
                    onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })}
                  />
                }
                label="Email Verified"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={prepareUpdate}
            variant="contained"
            disabled={isUpdating}
          >
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm User Update</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You are about to update the following fields for <strong>{user.email}</strong>:
          </Typography>

          <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2 }}>
            {Object.entries(pendingUpdates).map(([field, newValue]) => (
              <Box key={field} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {formatFieldName(field)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      textDecoration: 'line-through',
                      color: 'error.main',
                      fontFamily: 'monospace',
                    }}
                  >
                    {formatValue(user[field as keyof User])}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    →
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'success.main',
                      fontWeight: 'bold',
                      fontFamily: 'monospace',
                    }}
                  >
                    {formatValue(newValue)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            color="primary"
            disabled={isUpdating}
          >
            {isUpdating ? 'Updating...' : 'Confirm Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
